import {
  Button,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@mui/material'
import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'
import IcBaselineCancel from '~icons/ic/baseline-cancel'
import IcBaselineSearch from '~icons/ic/baseline-search'
import { track } from 'src/utils/tracker'

/**
 * A combination of search input and submit button
 */
export function SearchForm(
  props: StyleProps & {
    scene?: string
    keyword: string
    placeholder: string
    button: 'separated' | 'inline'
    onSubmit: (keyword: string | null) => void
    prepend?: ReactNode
  },
) {
  const [value, setValue] = useState<string>(() => props.keyword)
  const { t } = useTranslation(['market', 'common'])

  const onClickSearchButton = useCallback(() => {
    track('search_btn_click', { scene: props.scene })
  }, [props.scene])

  return (
    <form
      className={twMerge('flex gap-2', props.className)}
      style={props.style}
      action=""
      onSubmit={e => {
        e.preventDefault()
        props.onSubmit(value)
      }}
    >
      {props.prepend}
      <OutlinedInput
        placeholder={props.placeholder}
        className="flex-auto basis-[400px] rounded-lg text-primary h-12"
        startAdornment={
          <InputAdornment position="start">
            <IcBaselineSearch className="text-primary" />
          </InputAdornment>
        }
        endAdornment={
          <>
            {value ? (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    setValue('')
                    props.onSubmit(null)
                  }}
                >
                  <IcBaselineCancel />
                </IconButton>
              </InputAdornment>
            ) : null}
            {props.button === 'inline' && (
              <Button
                type="submit"
                variant="text"
                className="text-purple-700 text-base font-semibold font-roboto"
                onClick={onClickSearchButton}
              >
                {t('common:search')}
              </Button>
            )}
          </>
        }
        value={value}
        onChange={e => setValue(e.target.value)}
        inputProps={{
          inputMode: 'search',
        }}
      />
      {props.button === 'separated' && (
        <Button
          type="submit"
          variant="contained"
          className="justify-self-start font-semibold shrink-0 grow-1 text-lg whitespace-nowrap px-8 h-12 basis-full md:basis-auto rounded-lg"
          onClick={() => {
            track('search_btn_click', { scene: props.scene })
          }}
        >
          {t('common:search')}
        </Button>
      )}
    </form>
  )
}
